import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence, Variants } from 'framer-motion';
import { 
  ShoppingBag, ChefHat, Clock, MapPin, Phone, 
  Instagram, MessageCircle, ArrowRight, Menu, X, 
  Check, Star, Heart, Sparkles, Filter, ChevronRight,
  Truck, Award, ShieldCheck, UtensilsCrossed, Coffee, Play,
  Plus,
  Soup
} from 'lucide-react';

const MidBakeryLanding = () => {
  const [currentPage, setCurrentPage] = useState('home');
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (page: string) => {
    setCurrentPage(page);
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="bg-[#FFFCF7] text-[#2D2D2D] min-h-screen font-sans selection:bg-orange-600 selection:text-white flex flex-col overflow-x-hidden">
      
      {/* === ULTRA MODERN NAVBAR === */}
      <nav className={`fixed w-full z-50 transition-all duration-700 ${scrolled ? 'bg-white/90 backdrop-blur-2xl border-b border-orange-100 py-3 shadow-sm' : 'bg-transparent py-8'}`}>
        <div className="max-w-[90rem] mx-auto px-6 lg:px-12 flex justify-between items-center">
          
          <div onClick={() => handleNavClick('home')} className="flex items-center gap-3 cursor-pointer group z-50">
            <div className="relative">
              <div className="w-12 h-12 bg-stone-900 rounded-full flex items-center justify-center text-orange-500 transition-transform group-hover:rotate-[360deg] duration-1000">
                <Soup size={24} />
              </div>
              <div className="absolute -top-1 -right-1 w-4 h-4 bg-orange-600 rounded-full border-2 border-white"></div>
            </div>
            <div className="flex flex-col">
              <span className="font-serif text-3xl font-black tracking-tighter leading-none">Crust<span className="text-orange-600">.</span></span>
              <span className="text-[8px] tracking-[0.4em] text-stone-400 uppercase font-black">Artisan Bakes</span>
            </div>
          </div>
          
          <div className="hidden lg:flex items-center gap-12 text-[10px] font-black tracking-[0.2em] uppercase text-stone-500">
            {['home', 'shop', 'cakes', 'process', 'contact'].map((item) => (
              <button 
                key={item} 
                onClick={() => handleNavClick(item)}
                className={`transition-all hover:text-orange-600 relative py-2 ${currentPage === item ? 'text-stone-900' : ''}`}
              >
                {item === 'home' ? 'Index' : item === 'process' ? 'The Craft' : item}
                {currentPage === item && <motion.div layoutId="navline" className="absolute bottom-0 left-0 w-full h-1 bg-orange-600 rounded-full" />}
              </button>
            ))}
          </div>

          <div className="hidden lg:flex items-center gap-6">
            <button onClick={() => handleNavClick('shop')} className="flex items-center gap-3 bg-stone-900 text-white px-8 py-4 rounded-none font-black text-[10px] tracking-widest uppercase hover:bg-orange-600 transition-all shadow-2xl shadow-stone-200">
              Explore Shop <ArrowRight size={14}/>
            </button>
          </div>

          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden z-50 p-3 bg-stone-900 text-white rounded-full">
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div initial={{ opacity: 0, scale: 1.1 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 1.1 }} className="fixed inset-0 bg-[#FFFCF7] z-40 flex flex-col items-center justify-center gap-8">
               {['home', 'shop', 'cakes', 'process', 'contact'].map((item) => (
                 <button key={item} onClick={() => handleNavClick(item)} className="text-5xl font-serif font-black text-stone-900 hover:text-orange-600 lowercase tracking-tighter">
                   {item}.
                 </button>
               ))}
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* === CONTENT ROUTING === */}
      <main className="flex-grow pt-24">
        <AnimatePresence mode="wait">
          <motion.div key={currentPage} initial={{opacity:0, y:20}} animate={{opacity:1, y:0}} exit={{opacity:0, y:-20}} transition={{duration:0.6}}>
            {currentPage === 'home' && <HomeView setPage={handleNavClick} />}
            {currentPage === 'shop' && <ShopView />}
            {currentPage === 'cakes' && <CakesView setPage={handleNavClick} />}
            {currentPage === 'process' && <ProcessView />}
            {currentPage === 'contact' && <ContactView />}
          </motion.div>
        </AnimatePresence>
      </main>

      {/* === FOOTER === */}
      <footer className="bg-white border-t border-orange-100 pt-32 pb-12 mt-20">
        <div className="max-w-[90rem] mx-auto px-6 lg:px-12 grid grid-cols-1 md:grid-cols-12 gap-16 mb-24">
          <div className="md:col-span-5">
            <span className="font-serif text-5xl font-black tracking-tighter text-stone-900 mb-8 block">Crust<span className="text-orange-600">.</span></span>
            <p className="font-light text-xl leading-relaxed max-w-sm text-stone-500">Bringing the old-world charm of European baking to your doorstep with a modern twist.</p>
          </div>
          <div className="md:col-span-4">
            <h4 className="text-[10px] font-black tracking-[0.3em] uppercase mb-10 text-orange-600">The Menu</h4>
            <ul className="space-y-6 text-2xl font-serif italic text-stone-400">
              <li className="hover:text-stone-900 cursor-pointer transition-colors" onClick={() => handleNavClick('shop')}>Daily Sourdough</li>
              <li className="hover:text-stone-900 cursor-pointer transition-colors" onClick={() => handleNavClick('cakes')}>Custom Celebration</li>
              <li className="hover:text-stone-900 cursor-pointer transition-colors" onClick={() => handleNavClick('shop')}>French Pastries</li>
            </ul>
          </div>
          <div className="md:col-span-3">
            <h4 className="text-[10px] font-black tracking-[0.3em] uppercase mb-10 text-orange-600">Find Us</h4>
            <p className="text-lg font-medium mb-4">42nd Bakery Lane, City Center</p>
            <p className="text-stone-400">Open 8 AM — 10 PM</p>
            <div className="flex gap-4 mt-8">
              <Instagram size={24} className="hover:text-orange-600 cursor-pointer transition-colors" />
              <MessageCircle size={24} className="hover:text-orange-600 cursor-pointer transition-colors" />
            </div>
          </div>
        </div>
        <div className="max-w-[90rem] mx-auto px-6 text-center border-t border-orange-50 pt-12 flex flex-col md:flex-row justify-between text-[10px] font-black tracking-widest uppercase text-stone-400">
          <span>© 2026 Crust & Cream Artisan Bakehouse</span>
          <span className="mt-4 md:mt-0">Inspired by Heritage</span>
        </div>
      </footer>
    </div>
  );
};

// ==========================================
// 1. HOME VIEW (Massive & Different Design)
// ==========================================
const HomeView = ({ setPage }: { setPage: (page: string) => void }) => (
  <div className="-mt-24">
    {/* Hero Section: Big Typography & Overlapping Images */}
    <section className="relative min-h-screen flex items-center px-6 lg:px-12 bg-[#FFFAF3] overflow-hidden">
      <div className="absolute top-0 right-0 w-1/2 h-full bg-orange-100/20 skew-x-6 transform translate-x-20"></div>
      
      <div className="max-w-[90rem] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center w-full pt-32">
        <div className="lg:col-span-7 z-10">
          <motion.p initial={{opacity:0}} animate={{opacity:1}} transition={{delay:0.2}} className="text-orange-600 font-black tracking-[0.4em] text-xs uppercase mb-8">Established 2015 — City's Favorite</motion.p>
          <motion.h1 initial={{opacity:0, y:30}} animate={{opacity:1, y:0}} transition={{delay:0.4}} className="text-7xl md:text-[10rem] font-serif font-black text-stone-900 leading-[0.85] tracking-tighter mb-12">
            Baked<br/>For the<br/><span className="italic text-orange-600">Soul.</span>
          </motion.h1>
          <div className="flex flex-col md:flex-row gap-12 items-start">
            <p className="text-stone-500 font-light text-xl max-w-sm leading-relaxed border-l-2 border-orange-200 pl-8">We don't just bake bread; we cultivate culture through slow fermentation and heritage grains.</p>
            <button onClick={() => setPage('shop')} className="group relative bg-stone-900 text-white p-10 rounded-full hover:bg-orange-600 transition-all duration-500 shadow-2xl">
              <ArrowRight size={32} className="group-hover:translate-x-2 transition-transform" />
              <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 text-[10px] font-black uppercase tracking-widest text-stone-400 group-hover:text-orange-600">Enter Shop</span>
            </button>
          </div>
        </div>
        <div className="lg:col-span-5 relative">
          <div className="relative w-full aspect-[3/4] bg-stone-200 overflow-hidden shadow-2xl">
            <img src="https://tse1.mm.bing.net/th/id/OIP.LDPiNEaM8okGSYKSRn3dxwHaFj?pid=Api&P=0&h=180" alt="Main" className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-1000 scale-110 hover:scale-100" />
            <div className="absolute inset-0 border-[30px] border-white/10 pointer-events-none"></div>
          </div>
          <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-white p-4 shadow-2xl hidden md:block">
            <img src="https://images.pexels.com/photos/2135/food-france-morning-breakfast.jpg?auto=compress&cs=tinysrgb&w=600" alt="Sub" className="w-full h-full object-cover" />
          </div>
        </div>
      </div>
    </section>

    {/* Section 2: Huge Stats & Philosophy */}
    <section className="py-40 px-6 bg-stone-900 text-white overflow-hidden relative">
      <div className="absolute top-1/2 left-0 text-[20vw] font-black text-white/5 whitespace-nowrap -translate-y-1/2 pointer-events-none">THE HERITAGE</div>
      <div className="max-w-[90rem] mx-auto grid grid-cols-1 md:grid-cols-3 gap-20 items-center relative z-10">
        <div>
          <h2 className="text-5xl font-serif italic mb-8">Pure <br/>Ingredients.</h2>
          <p className="text-stone-400 font-light text-lg">No chemicals, no improvers. Just stone-ground flour, Himalayan salt, and our 10-year-old sourdough mother culture.</p>
        </div>
        <div className="md:col-span-2 grid grid-cols-2 gap-12">
          <div>
            <span className="text-8xl font-black text-orange-600 leading-none">0%</span>
            <p className="uppercase tracking-[0.2em] text-[10px] font-bold mt-4 text-stone-500">Preservatives used in our kitchen</p>
          </div>
          <div>
            <span className="text-8xl font-black text-orange-600 leading-none">48h</span>
            <p className="uppercase tracking-[0.2em] text-[10px] font-bold mt-4 text-stone-500">Slow fermentation for every loaf</p>
          </div>
        </div>
      </div>
    </section>

    {/* Section 3: Horizontal Featured Products */}
    <section className="py-40 bg-white">
      <div className="max-w-[90rem] mx-auto px-6">
        <div className="flex justify-between items-end mb-24">
          <h2 className="text-6xl md:text-8xl font-serif font-black tracking-tighter">Season's <br/><span className="text-orange-600 italic">Signature.</span></h2>
          <button onClick={() => setPage('shop')} className="text-stone-400 hover:text-stone-900 font-black text-[10px] tracking-widest uppercase flex items-center gap-2 border-b-2 border-stone-100 pb-2">View Full Menu <ChevronRight size={14}/></button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-16">
          {[
            { title: "Lavender Lemon Tart", desc: "Shortcrust pastry with organic lavender & fresh lemon curd.", img: "https://images.pexels.com/photos/461060/pexels-photo-461060.jpeg?auto=compress&cs=tinysrgb&w=800" },
            { title: "Artisan Olive Focaccia", desc: "Hand-dimpled dough with Kalamata olives & rosemary.", img: "https://images.pexels.com/photos/1586947/pexels-photo-1586947.jpeg?auto=compress&cs=tinysrgb&w=800" },
            { title: "Triple Truffle Cake", desc: "Three layers of Belgian dark chocolate & hazelnut praline.", img: "https://images.pexels.com/photos/4109998/pexels-photo-4109998.jpeg?auto=compress&cs=tinysrgb&w=800" }
          ].map((item, i) => (
            <div key={i} className="group cursor-pointer">
              <div className="aspect-square bg-stone-100 overflow-hidden mb-8 relative">
                <img src={item.img} alt={item.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000" />
                <div className="absolute top-6 right-6 w-12 h-12 bg-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <Plus size={20} className="text-orange-600" />
                </div>
              </div>
              <h3 className="text-3xl font-serif mb-3">{item.title}</h3>
              <p className="text-stone-500 font-light">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Section 4: Daily Bake Timeline (New) */}
    <section className="py-32 bg-[#FFFCF7] border-y border-orange-100">
      <div className="max-w-4xl mx-auto px-6">
        <h2 className="text-center text-4xl font-serif mb-20 italic">The Daily Rhythm</h2>
        <div className="space-y-12">
          {[
            { time: "04:00 AM", event: "Ovens fired up with our heritage sourdough batches." },
            { time: "07:30 AM", event: "Freshly laminated croissants ready for the first coffee." },
            { time: "11:00 AM", event: "Mid-day bakes: Baguettes, Focaccia & Savory Tarts." },
            { time: "03:00 PM", event: "Cake gallery updated with today's designer bento cakes." }
          ].map((item, i) => (
            <div key={i} className="flex gap-12 items-center border-b border-orange-100 pb-8 last:border-0">
              <span className="text-3xl font-black text-orange-600 w-32">{item.time}</span>
              <p className="text-xl font-light text-stone-600">{item.event}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  </div>
);

// ==========================================
// 2. THE SHOP VIEW (Clean Grid)
// ==========================================
const ShopView = () => (
  <div className="py-20 px-6 lg:px-12 max-w-[90rem] mx-auto min-h-screen">
    <div className="flex flex-col md:flex-row justify-between items-center mb-24 border-b border-orange-100 pb-12 gap-8">
      <h1 className="text-7xl font-serif font-black tracking-tighter">The <span className="text-orange-600">Shop.</span></h1>
      <div className="flex gap-4">
        {['all', 'breads', 'pastries', 'jars'].map(cat => (
          <button key={cat} className="px-6 py-2 rounded-full border border-stone-200 text-[10px] font-black uppercase tracking-widest hover:bg-stone-900 hover:text-white transition-all">{cat}</button>
        ))}
      </div>
    </div>
    
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-10 gap-y-20">
      {[...Array(8)].map((_, i) => (
        <div key={i} className="group">
          <div className="aspect-[3/4] bg-stone-100 overflow-hidden mb-6 relative">
            <img src={`https://images.pexels.com/photos/${1000 + i * 100}/pexels-photo-${1000 + i * 100}.jpeg?auto=compress&cs=tinysrgb&w=600`} alt="Product" className="w-full h-full object-cover" />
            <button className="absolute bottom-0 right-0 bg-stone-900 text-white p-6 opacity-0 group-hover:opacity-100 transition-opacity"><ShoppingBag size={24}/></button>
          </div>
          <h3 className="text-xl font-bold mb-1">Artisan Special {i+1}</h3>
          <p className="text-orange-600 font-black">₹{199 + i * 50}</p>
        </div>
      ))}
    </div>
  </div>
);

// ==========================================
// 3. CAKES VIEW (Designer Focus)
// ==========================================
const CakesView = ({ setPage }: { setPage: (page: string) => void }) => (
  <div className="pb-20">
    <section className="h-[70vh] relative flex items-center justify-center text-white text-center">
      <img src="https://images.pexels.com/photos/1702373/pexels-photo-1702373.jpeg?auto=compress&cs=tinysrgb&w=1200" alt="Cake Hero" className="absolute inset-0 w-full h-full object-cover brightness-50" />
      <div className="relative z-10 max-w-4xl px-6">
        <span className="text-orange-400 font-black tracking-[0.4em] text-xs uppercase mb-6 block">Edible Art</span>
        <h1 className="text-6xl md:text-9xl font-serif font-black tracking-tighter leading-none mb-12">Designer<br/>Milestones.</h1>
        <button onClick={() => setPage('contact')} className="bg-orange-600 text-white px-12 py-5 rounded-full font-black uppercase text-xs tracking-widest hover:bg-white hover:text-stone-900 transition-all">Consult a Designer</button>
      </div>
    </section>

    <div className="max-w-[90rem] mx-auto px-6 py-40">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-32 items-center">
        <div>
          <h2 className="text-5xl font-serif mb-12 italic">Couture for your table.</h2>
          <div className="space-y-12">
            {[
              { t: "The Wedding Series", d: "Tiered masterpieces with hand-crafted sugar flowers and luxury fillings." },
              { t: "Bento Celebrations", d: "Minimalist 4-inch cakes for intimate moments and surprises." },
              { t: "Corporate Hampers", d: "Custom branded pastry boxes for team celebrations and gifting." }
            ].map((item, i) => (
              <div key={i} className="border-l-4 border-orange-600 pl-8">
                <h4 className="text-2xl font-bold mb-2">{item.t}</h4>
                <p className="text-stone-500 font-light">{item.d}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-8">
           <img src="https://images.pexels.com/photos/4109998/pexels-photo-4109998.jpeg?auto=compress&cs=tinysrgb&w=600" className="w-full h-80 object-cover mt-12 shadow-2xl" alt="1" />
           <img src="https://images.pexels.com/photos/1126359/pexels-photo-1126359.jpeg?auto=compress&cs=tinysrgb&w=600" className="w-full h-80 object-cover shadow-2xl" alt="2" />
        </div>
      </div>
    </div>
  </div>
);

// ==========================================
// 4. THE CRAFT (New Process View)
// ==========================================
const ProcessView = () => (
  <div className="py-20 px-6 max-w-[90rem] mx-auto">
     <div className="text-center mb-40">
       <span className="text-orange-600 font-black tracking-[0.4em] text-xs uppercase mb-8 block">The Secret Sauce</span>
       <h1 className="text-7xl md:text-[10rem] font-serif font-black tracking-tighter leading-none mb-12 italic">Patience.</h1>
       <p className="max-w-2xl mx-auto text-xl text-stone-500 font-light">We believe that great things take time. Especially bread. Here's how we create magic every day.</p>
     </div>

     <div className="space-y-40">
       {[
         { n: "01", t: "The Sourdough Mother", d: "Our sourdough starter is a living piece of history, nurtured daily for over 10 years to give our bread its signature tang and structure.", img: "https://images.pexels.com/photos/1586947/pexels-photo-1586947.jpeg?auto=compress&cs=tinysrgb&w=1200" },
         { n: "02", t: "Stone-Ground Flour", d: "We source our flour from local mills that use traditional stone-grinding to preserve nutrients and natural oils.", img: "https://images.pexels.com/photos/3814893/pexels-photo-3814893.jpeg?auto=compress&cs=tinysrgb&w=1200" },
         { n: "03", t: "Manual Lamination", d: "Our croissants are hand-folded to create precisely 256 layers of flaky, buttery perfection.", img: "https://images.pexels.com/photos/3826678/pexels-photo-3826678.jpeg?auto=compress&cs=tinysrgb&w=1200" }
       ].map((step, i) => (
         <div key={i} className={`grid grid-cols-1 lg:grid-cols-12 gap-20 items-center ${i % 2 !== 0 ? 'lg:flex-row-reverse' : ''}`}>
           <div className={`lg:col-span-5 ${i % 2 !== 0 ? 'lg:order-2' : 'lg:order-1'}`}>
             <span className="text-9xl font-black text-orange-100 leading-none block mb-8">{step.n}</span>
             <h3 className="text-5xl font-serif font-black mb-8 leading-tight">{step.t}</h3>
             <p className="text-stone-500 text-xl font-light leading-relaxed">{step.d}</p>
           </div>
           <div className={`lg:col-span-7 ${i % 2 !== 0 ? 'lg:order-1' : 'lg:order-2'}`}>
             <img src={step.img} alt={step.t} className="w-full h-[600px] object-cover shadow-2xl rounded-sm" />
           </div>
         </div>
       ))}
     </div>
  </div>
);

// ==========================================
// 5. CONTACT VIEW (Expanded)
// ==========================================
const ContactView = () => (
  <div className="py-40 px-6 max-w-[90rem] mx-auto">
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-32">
      <div>
        <h1 className="text-7xl font-serif font-black tracking-tighter leading-[0.9] mb-12">Connect with the <br/><span className="text-orange-600 italic">Bakehouse.</span></h1>
        <p className="text-stone-500 text-xl font-light mb-20 leading-relaxed">Whether it's a large corporate order or a simple question about our flavors, we're here to help.</p>
        
        <div className="space-y-16">
          <div className="flex gap-10 items-start">
             <div className="p-6 bg-white shadow-2xl rounded-full text-orange-600"><MapPin size={32}/></div>
             <div>
                <h4 className="font-black text-xs uppercase tracking-widest text-stone-400 mb-4">Our Home</h4>
                <p className="text-2xl font-bold">42nd Bakery Lane, City Center Park, Ground Floor Arcade</p>
             </div>
          </div>
          <div className="flex gap-10 items-start">
             <div className="p-6 bg-white shadow-2xl rounded-full text-orange-600"><MessageCircle size={32}/></div>
             <div>
                <h4 className="font-black text-xs uppercase tracking-widest text-stone-400 mb-4">Direct Link</h4>
                <p className="text-2xl font-bold">+91 98765 43210</p>
                <p className="text-orange-600 font-bold mt-2 hover:underline cursor-pointer">Chat on WhatsApp</p>
             </div>
          </div>
        </div>
      </div>

      <div className="bg-stone-900 p-12 lg:p-20 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-orange-600/10 blur-[120px]"></div>
        <h3 className="text-4xl font-serif italic text-white mb-12">Leave a Message</h3>
        <form className="space-y-10" onSubmit={(e) => { e.preventDefault(); alert("Message Received!"); }}>
           <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              <input type="text" required placeholder="Full Name" className="w-full bg-transparent border-b border-stone-700 py-4 text-white focus:outline-none focus:border-orange-500 transition-colors placeholder-stone-600 font-medium" />
              <input type="tel" required placeholder="Phone Number" className="w-full bg-transparent border-b border-stone-700 py-4 text-white focus:outline-none focus:border-orange-500 transition-colors placeholder-stone-600 font-medium" />
           </div>
           <input type="email" placeholder="Email Address (Optional)" className="w-full bg-transparent border-b border-stone-700 py-4 text-white focus:outline-none focus:border-orange-500 transition-colors placeholder-stone-600 font-medium" />
           <textarea placeholder="How can we make your day sweeter?" className="w-full bg-transparent border-b border-stone-700 py-4 text-white focus:outline-none focus:border-orange-500 h-32 placeholder-stone-600 font-medium"></textarea>
           <button type="submit" className="w-full bg-orange-600 text-white py-6 rounded-none font-black uppercase text-xs tracking-widest hover:bg-white hover:text-stone-900 transition-all">Send to the Bakehouse</button>
        </form>
      </div>
    </div>
  </div>
);

export default MidBakeryLanding;